/**
 * Notification Sound Utility
 * Generates short audio tones for notifications without external files
 * Respects user preference stored in localStorage
 */

const SOUND_ENABLED_KEY = 'acquizgroup_notification_sounds_enabled';

const AudioCtx = typeof window !== 'undefined' ? (window.AudioContext || (window as any).webkitAudioContext) : null;

let audioCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (!AudioCtx) return null;
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioCtx();
  }
  return audioCtx;
}

/** Check if notification sounds are enabled */
export function areSoundsEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  const stored = localStorage.getItem(SOUND_ENABLED_KEY);
  // Default to enabled if not set
  return stored === null ? true : stored === 'true';
}

/** Set notification sounds preference */
export function setSoundsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
}

/** Short "pop" sound for new messages */
export function playMessageSound() {
  if (!areSoundsEnabled()) return;

  const ctx = getCtx();
  if (!ctx) return;

  // Resume if suspended (browser autoplay policy)
  if (ctx.state === 'suspended') ctx.resume();

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.type = 'sine';
  osc.frequency.setValueAtTime(880, ctx.currentTime);       // A5
  osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.05); // C#6

  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.2);
}

/** Two-tone chime for contact request notifications */
export function playNotificationSound() {
  if (!areSoundsEnabled()) return;

  const ctx = getCtx();
  if (!ctx) return;

  if (ctx.state === 'suspended') ctx.resume();

  // First tone
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(587, ctx.currentTime); // D5
  gain1.gain.setValueAtTime(0.15, ctx.currentTime);
  gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
  osc1.start(ctx.currentTime);
  osc1.stop(ctx.currentTime + 0.25);

  // Second tone (higher)
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.15); // A5
  gain2.gain.setValueAtTime(0, ctx.currentTime);
  gain2.gain.setValueAtTime(0.15, ctx.currentTime + 0.15);
  gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
  osc2.start(ctx.currentTime + 0.15);
  osc2.stop(ctx.currentTime + 0.45);
}
